import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { checkIsAdmin } from "@/lib/admin.functions";
import { getUsageAlerts, type UsageAlertRow } from "@/lib/usage-alerts.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Search, ArrowLeft, Mail } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin_/usage-alerts")({
  component: UsageAlertsPage,
});

function UsageAlertsPage() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<UsageAlertRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [thresholdFilter, setThresholdFilter] = useState<string>("all");
  const [periodFilter, setPeriodFilter] = useState<"current" | "all">("current");

  useEffect(() => {
    if (!user) return;
    async function load() {
      try {
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;
        if (!accessToken) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        const adminCheck = await checkIsAdmin({
          data: undefined as any,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!adminCheck.isAdmin) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        setIsAdmin(true);
        const data = await getUsageAlerts({
          data: undefined as any,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setRows(data);
      } catch {
        setIsAdmin(false);
      }
      setLoading(false);
    }
    load();
  }, [user]);

  const thresholds = useMemo(() => {
    const set = new Set<string>();
    (rows ?? []).forEach((r) => set.add(r.threshold));
    return Array.from(set).sort();
  }, [rows]);

  // "Current period" = the most recent period_start per merchant
  const filtered = useMemo(() => {
    if (!rows) return [];
    let r = rows;
    if (periodFilter === "current") {
      const latestByMerchant = new Map<string, string>();
      rows.forEach((row) => {
        const cur = latestByMerchant.get(row.merchantId);
        if (!cur || row.periodStart > cur) latestByMerchant.set(row.merchantId, row.periodStart);
      });
      r = r.filter((row) => latestByMerchant.get(row.merchantId) === row.periodStart);
    }
    if (thresholdFilter !== "all") {
      r = r.filter((row) => row.threshold === thresholdFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(
        (row) =>
          row.merchantName.toLowerCase().includes(q) ||
          row.merchantSlug.toLowerCase().includes(q) ||
          row.recipientEmail.toLowerCase().includes(q)
      );
    }
    return r;
  }, [rows, search, thresholdFilter, periodFilter]);

  // Aggregates for the active filter
  const stats = useMemo(() => {
    const uniqueMerchants = new Set(filtered.map((r) => r.merchantId)).size;
    const byThreshold: Record<string, number> = {};
    filtered.forEach((r) => {
      byThreshold[r.threshold] = (byThreshold[r.threshold] || 0) + 1;
    });
    return { total: filtered.length, uniqueMerchants, byThreshold };
  }, [filtered]);

  const thresholdTone = (t: string) => {
    if (t.includes("100") || t.includes("over")) return "bg-destructive/10 text-destructive border-destructive/30";
    if (t.includes("90")) return "bg-destructive/10 text-destructive border-destructive/30";
    if (t.includes("70") || t.includes("75") || t.includes("80")) return "bg-tack-amber/10 text-tack-amber border-tack-amber/30";
    return "bg-muted text-muted-foreground border-border";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <ShieldCheck className="size-12 mx-auto text-muted-foreground/40" />
          <h1 className="text-xl font-semibold">Access Denied</h1>
          <p className="text-sm text-muted-foreground">You don't have admin access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2"
            >
              <ArrowLeft size={14} /> Back to admin
            </Link>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Mail size={22} /> Usage alert deliveries
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Audit which merchants received which threshold emails. Default view shows the current billing period per merchant.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Emails (filtered)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">{stats.total.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Unique merchants</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">{stats.uniqueMerchants.toLocaleString()}</div>
            </CardContent>
          </Card>
          {Object.entries(stats.byThreshold)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([t, n]) => (
              <Card key={t}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground">Threshold: {t}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold tabular-nums">{n.toLocaleString()}</div>
                </CardContent>
              </Card>
            ))}
        </div>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search merchant or email…"
                  className="pl-9"
                />
              </div>

              <div className="flex gap-1">
                <Button
                  variant={periodFilter === "current" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPeriodFilter("current")}
                >
                  Current period
                </Button>
                <Button
                  variant={periodFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPeriodFilter("all")}
                >
                  All time
                </Button>
              </div>

              <div className="flex gap-1 flex-wrap">
                <Button
                  variant={thresholdFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setThresholdFilter("all")}
                >
                  All thresholds
                </Button>
                {thresholds.map((t) => (
                  <Button
                    key={t}
                    variant={thresholdFilter === t ? "default" : "outline"}
                    size="sm"
                    onClick={() => setThresholdFilter(t)}
                  >
                    {t}
                  </Button>
                ))}
              </div>
            </div>

            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sent</TableHead>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Threshold</TableHead>
                    <TableHead>Period start</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8 text-sm">
                        No alerts match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(r.sentAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{r.merchantName}</div>
                          <div className="text-xs text-muted-foreground">{r.merchantSlug}</div>
                        </TableCell>
                        <TableCell className="text-sm">{r.recipientEmail}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={thresholdTone(r.threshold)}>
                            {r.threshold}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(r.periodStart).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="text-xs text-muted-foreground">
              Showing {filtered.length.toLocaleString()} of {(rows ?? []).length.toLocaleString()} total records (max 1000).
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
